<template>
  <div class="modal fade" id="over-products-modal" role="dialog" data-backdrop="static" data-keyboard="false" @click.stop>
    <div class="modal-dialog modal-lg" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h3>
            <b>出品商品の追加購入</b>
            <b v-if="neededProductCount > 0" class="text-danger mr-l-10"><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> 出品可能数が不足しています。出品数の追加をしてください。</b>
          </h3>
          <button type="button" class="close close-btn" data-dismiss="modal" @click="$emit('close')"> &times;</button>
        </div>
        <div class="modal-body">
          <div class="stats-grid">
            <div class="stat-box">
              <div class="stat-label">残り出品可能数</div>
              <div class="stat-value">{{ remainingProduct }}商品</div>
            </div>
            <div class="stat-box">
              <div class="stat-label">最大商品登録可能数</div>
              <div class="stat-value">{{ maxListingAmount }}商品</div>
            </div>
            <div v-if="neededProductCount > 0" class="stat-box highlight-danger">
              <div class="stat-label">不足している商品数</div>
              <div class="stat-value">{{ neededProductCount }}商品</div>
            </div>
          </div>
          <div class="upgrade-section">
            <h4><b>追加する商品数を選択して下さい。</b></h4>
            <div class="plan-list" id="planList">
              <div
                v-for="(plan, index) in listPlans"
                :key="index" class="plan-item"
                :class="{ 'checked' : plan.checked, 'optimal' : plan.optimal}"
                @click.stop="handleSelectPlan(plan)"
              >
                <input type="checkbox" class="plan-checkbox" v-model="plan.checked">
                <div class="plan-name" v-html="plan.salMonPlanName"></div>
                <div class="plan-price">¥{{formatNumber(plan.salMonPlanAmount)}}（税込み）</div>
                <div class="plan-desc" v-html="plan.salMonPlanDescription"></div>
                <div class="plan-qty-wrapper">
                  <input type="number" min="1" max="99" class="plan-qty-input" :disabled="!plan.checked" v-model="plan.quantity" @click.stop>
                  <span v-if="plan.optimal" class="optimal-badge">おすすめ</span>
                </div>
              </div>
            </div>

            <div class="summary-section">
              <div class="summary-item">
                <span>合計追加出品数:</span>
                <span>{{ totalItemsAdded }}商品</span>
              </div>
              <div class="summary-total">
                <span>合計料金:</span>
                <span>¥{{ totalPrice }}（税込み）</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="footer-note">※「クレジット決済」を押すと、Stripeの決済画面へ移動します。</div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <button class="btn btn-back" @click="$emit('close')">戻る</button>
            <div style="display: flex; gap: 10px; justify-content: flex-end; align-items: center;">
              <button class="btn btn-primary" :disabled="disabledSubmit" @click="showModalConfirm = true">売上金から支払う</button>
              <button class="btn btn-primary" :disabled="disabledSubmit" @click="handleGetCheckoutSession">クレジット決済</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <modal-confirm v-if="showModalConfirm" @confirm="handleSubmitPaymentFromRevenue" @close="closeModal" :message="planUpdateMessage"></modal-confirm>
  </div>
</template>

<script>
import Util from '@/common/utility'
import ModalConfirm from '@/components/ModalConfirm'
import PlanService from '@/services/planservice'

export default {
  props: {
    maxListingAmount: {
      type: Number,
      default: 0
    },
    remainingProduct: {
      type: Number,
      default: 0
    },
    neededProductCount: {
      type: Number,
      default: 0
    },
    listPlans: {
      type: Array,
      default: () => []
    },
    pendingAsins: {
      type: Array,
      default: () => []
    }
  },
  components: {
    ModalConfirm
  },
  data () {
    return {
      loader: null,
      showModalConfirm: false
    }
  },
  computed: {
    disabledSubmit () {
      return this.totalItemsAdded <= 0 || this.totalItemsAdded < this.neededProductCount
    },
    totalItemsAdded () {
      return this.listPlans.reduce((total, plan) => total + (plan.checked ? plan.salMonPlanUpperlimit * plan.quantity : 0), 0)
    },
    totalPrice () {
      const total = this.listPlans.reduce((total, plan) => total + (plan.checked ? plan.salMonPlanAmount * plan.quantity : 0), 0)
      return this.formatNumber(total)
    },
    planUpdateMessage () {
      return `
        <p>商品数追加に必要な金額：¥${this.totalPrice}(税込み) を売上からマイナスをいたします。</p>
        <p>追加後に商品数を元に戻すことはできません。</p>
      `
    }
  },
  methods: {
    closeModal () {
      $('#confirm-alert').modal('hide')
      this.showModalConfirm = false
    },
    formatNumber (number) {
      return Util.formatMoney(number)
    },
    handleSelectPlan (plan) {
      plan.checked = !plan.checked
      plan.quantity = plan.checked ? (plan.quantity || 1) : 0
    },
    validateSelection () {
      if (this.disabledSubmit) {
        alert(`不足数を満たすためには、合計で${this.neededProductCount}件以上の商品数を追加する必要があります。現在の選択では${this.totalItemsAdded}件です。`)
        return false
      }

      const hasInvalid = this.listPlans.some(plan => {
        return plan.checked && (!plan.quantity || plan.quantity < 1)
      })

      if (hasInvalid) {
        alert(`数量は1以上を入力してください。`)
        return false
      }

      return true
    },
    async handleGetCheckoutSession () {
      if (!this.validateSelection()) return

      const selectedPlans = this.listPlans.filter(plan => plan.checked).map(plan => ({
        sku: plan.salMonPlanScript,
        quantity: plan.quantity
      }))

      this.loader = this.$loading.show()

      await PlanService.getCheckoutSessionMulti({ plans: selectedPlans, redirectPath: this.$route.path.replace(/^\//, '') })
        .then(response => {
          if (response && response.data) {
            if (this.pendingAsins.length > 0) {
              sessionStorage.setItem('pendingSpeedReviewAsins', JSON.stringify(this.pendingAsins))
            }
            this.$emit('close')
            location.href = response.data
          }
        })
        .catch(error => {
          this.$store.commit('toggleModal', {
            show: true,
            message: error
          })
        })
        .finally(() => {
          this.loader && this.loader.hide()
        })
    },
    async handleSubmitPaymentFromRevenue () {
      if (!this.validateSelection()) return
      this.closeModal()
      this.loader = this.$loading.show()

      const selectedPlans = this.listPlans.filter(plan => plan.checked)
      await PlanService.updatePlanBySales({ plans: selectedPlans })
        .then((response) => {
          this.$emit('close')
          this.$emit('updatePlanStatus')
        })
        .catch((err) => {
          this.$store.commit('toggleModal', {
            show: true,
            message: err
          })
        })
        .finally(() => {
          this.loader && this.loader.hide()
        })
    }
  },
  mounted () {
    $('#over-products-modal').modal('show')
  },
  beforeDestroy () {
    this.$emit('close')
  }
}
</script>
<style scope>
#over-products-modal {
 .modal-lg {
    width: 80%;
    min-width: none;
    max-width: 1000px;
  }
  .modal-header::before, .modal-header::after {
    content: none;
  }
  .modal-header {
    padding: 15px 20px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafafa;
  }
  .modal-header h3 {
    font-size: 16px;
    color: #333;
  }
  .close-btn {
    cursor: pointer;
    font-size: 20px;
    color: #ccc;
    border: none;
    background: none;
  }

  .close-btn:hover {
    color: #333;
  }

  .stats-grid {
    display: flex;
    gap: 15px;
    margin-bottom: 25px;
  }

  .stat-box {
    flex: 1;
    border: 1px solid #ddd;
    padding: 15px;
    border-radius: 5px;
    text-align: center;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .stat-box.highlight-danger {
    border-color: #d9534f;
    background: #fdf2f2;
  }

  .stat-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 5px;
  }

  .stat-value {
    font-size: 20px;
    font-weight: bold;
    color: #333;
  }

  .highlight-danger .stat-value {
    color: #d9534f;
  }

  .upgrade-section h4 {
    font-size: 14px;
    margin-bottom: 15px;
    color: #444;
    border-left: 3px solid #3b98d4;
    padding-left: 10px;
  }

  .plan-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .plan-item {
    display: flex;
    align-items: center;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    background: #fff;
    gap: 12px;
  }

  .plan-item.checked {
    background: #eef7f2 !important;
    border-color: #28a745 !important;
  }

  .plan-item:hover {
    border-color: #3b98d4;
  }

  .optimal-badge {
    position: absolute;
    top: -10px;
    left: 15px;
    background: #28a745;
    color: #fff;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: bold;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .plan-checkbox {
    min-width: 20px;
  }

  .plan-name {
    width: 180px;
    font-weight: bold;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .plan-price {
    width: 200px;
    color: #d9534f;
    font-weight: bold;
    text-align: right;
  }

  .plan-desc {
    flex: 1;
    font-size: 13px;
    color: #555;
  }

  .plan-qty-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 120px;
  }

  .plan-qty-input {
    width: 50px;
    padding: 5px;
    border: 1px solid #ddd;
    border-radius: 3px;
    text-align: center;
    font-size: 13px;
  }

  .plan-qty-input:disabled {
    background: #f5f5f5;
    color: #999;
  }

  .plan-qty-total {
    font-size: 13px;
    color: #666;
    min-width: 60px;
    text-align: right;
  }

  .modal-footer {
    padding: 15px 20px;
    background: #fafafa;
    border-top: 1px solid #eee;
  }

  .footer-note {
    text-align: left;
    font-size: 12px;
    color: #666;
    margin-right: auto;
    margin-bottom: 10px;
  }

  .summary-section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #ddd;
  }

  .summary-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 13px;
  }

  .summary-total {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 16px;
    font-weight: bold;
    color: #d9534f;
  }

  .btn-back {
    background: #d9534f;
    color: #fff;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ModalAddListings.vue